// The basket as the pages around Baluu's web components see it: whether a
// surface lets a visitor gather several things into one checkout, where the
// basket is kept, and how a page reads its count and opens it. Shared by the
// components (apps/webcomponents) and the pages that host them (Website v2's
// navbar), so the two can't disagree about the names. Pure, no DOM.

/**
 * Whether a surface offers the basket, as the components' `basket`
 * attribute (and `openPurchase`'s `basket` option) says it:
 *
 * - `"off"` (or no attribute): one thing at a time — "Book now" / "Buy now"
 *   go straight to payment with that one item, as every embed has since the
 *   basket was switched off for the components (2026-03).
 * - `"on"`: "Add to basket" beside "Book now", and the components' own
 *   floating basket button (`<baluu-basket>`).
 * - `"page"`: the same, but the page shows its own basket button (Website
 *   v2's navbar) and opens the basket through `openBasket`; the components'
 *   floating button stays hidden.
 *
 * A school that turned Basket Checkout off (or whose setting isn't known
 * yet) is single-item on every surface (`basketModeFor`).
 */
export type BasketMode = "off" | "on" | "page";

/** localStorage key the basket lives under (shared with the hosted widget's
 *  store, which persists the same shape on its own origin). */
export const BASKET_STORAGE_KEY = "baluu-basket";

/** Fired on `window` whenever the components change the basket. */
export const BASKET_UPDATED_EVENT = "baluu:basket-updated";

/** Fired on `window` to open the basket drawer. */
export const OPEN_BASKET_EVENT = "baluu:open-basket-drawer";

/** The basket's element (the drawer, and its floating button in `"on"`). */
export const BASKET_ELEMENT = "baluu-basket";

/** A `basket` attribute as the mode it names; anything else is off. */
export function parseBasketMode(value: unknown): BasketMode {
  return value === "on" || value === "page" ? value : "off";
}

/**
 * The basket a surface actually offers: the surface's own mode, only once
 * the school's setting is known to allow it (`true`). `false` (Basket
 * Checkout off, or a school that holds funds) and `undefined` (its
 * settings haven't arrived, or couldn't be had) are both one thing at a
 * time: a basket is never offered before we know the school allows one.
 */
export function basketModeFor(surface: BasketMode, schoolBasketEnabled: boolean | undefined): BasketMode {
  return schoolBasketEnabled === true ? surface : "off";
}

/** One basket line, as far as counting reads it. */
export type CountableBasketLine = { schoolId?: unknown; quantity?: unknown };

/**
 * How many things a school's basket holds: each line counts its quantity,
 * and a line without a usable one counts once. THE count rule — the
 * components' basket button, their overlays and Website v2's navbar all
 * read it, so they never show different numbers. Without a school, every
 * line counts.
 */
export function countBasketItems(items: readonly unknown[], schoolId?: string): number {
  return items.reduce<number>((sum, item) => {
    if (!item || typeof item !== "object") return sum;
    const { schoolId: owner, quantity } = item as CountableBasketLine;
    if (schoolId !== undefined && owner !== schoolId) return sum;
    return sum + (typeof quantity === "number" && quantity > 0 ? quantity : 1);
  }, 0);
}

/** How many things a school's basket holds, from the stored basket's raw
 *  text (`countBasketItems`). Unreadable or missing is an empty basket. */
export function storedBasketCount(raw: string | null | undefined, schoolId: string): number {
  if (!raw || !schoolId) return 0;
  try {
    const items: unknown = JSON.parse(raw)?.state?.items;
    return Array.isArray(items) ? countBasketItems(items, schoolId) : 0;
  } catch {
    return 0;
  }
}

/** What opening the basket takes (the bundle's `openBasket`). */
export type OpenBasketOptions = {
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  /** Who shows the basket's button; `"page"` (the default) when the page
   *  has its own. */
  mode?: Exclude<BasketMode, "off">;
};
